// src/utils/grnPlan.js
// D-292 (Stabilization P1) — pure rules for the store GRN screen.
//
// WHY THIS FILE EXISTS
// ReceiveMaterial.js had two near-copies of the GRN logic (Submit and Short
// Close). They had already drifted, and both decided which item gets the
// stock with a matcher that could pick the WRONG item when item codes were
// shared (D-309). This file holds that logic ONCE, with no Firebase calls,
// so it is tested by Jest (src/utils/grnPlan.test.js) and used by both
// buttons. The service (purchaseReceiptService.completeStoreGRN) then writes
// everything in ONE transaction.

import { checkQtyForUnit } from "./units";
import { matchItem } from "./itemMatch";

export const roundMoney = (n) => Math.round(Number(n || 0) * 100) / 100;

/*
  D-291 (P3d-1) - the STOCK rate a PO line suggests at GRN / Direct Site
  finalize, and the bill line's rate before GST.

  Rate before GST AFTER the line's discount, then:
    itcClaimed = true  -> that rate (the GST comes back as input credit,
                          so it is not part of the stock's cost)
    itcClaimed = false -> that rate + the line's GST (GST is a cost)
  "Rate not agreed yet" -> 0 (no pre-fill; the rate is typed).
  Freight is never in it (reference only).

  purchaseOrderService.grnRateFromPOLine() is today's GST-inclusive rate =
  this with itcClaimed false. It moves to this function, with the company's
  ITC setting, ONLY in the same change that moves the payable from the GRN to
  the purchase bill (P3d-4) - never before, or the khaata would be short.
*/
export const lineDiscountTotal = (baseRate, qty, discountType, discountValue) => {
  const subtotal = Number(baseRate || 0) * Number(qty || 0);
  const value    = Number(discountValue || 0);
  if (discountType === "percent") return subtotal * (value / 100);
  return Math.min(value, subtotal); // flat Rs. for the whole line, never more than it
};

export const stockRateFromPOLine = (line = {}, { itcClaimed = false } = {}) => {
  if (line.rate_not_agreed === true) return 0;
  const qty  = Number(line.ordered_qty ?? line.qty ?? 0);
  const base = Number(line.base_rate || line.rate || 0);
  if (!(base > 0)) return 0;
  const discountPerUnit = qty > 0
    ? lineDiscountTotal(base, qty, line.discount_type, line.discount_value) / qty
    : 0;
  const afterDiscount = Math.max(base - discountPerUnit, 0);
  if (itcClaimed) return Number(afterDiscount.toFixed(2));
  const gst = Number(line.gst_percent || 0);
  return Number((afterDiscount * (1 + gst / 100)).toFixed(2));
};

// Same status words the GRN has always stored (item_grn_status).
export const getItemGRNStatus = (received, ordered) => {
  if (ordered === 0)        return "over";   // extra item not in the PO
  if (received < ordered)   return "short";
  if (received === ordered) return "exact";
  return "over";
};

// A custom item (typed by hand, never in Item Master) is received for the
// record and the bill only — it never touches central stock. Older lines may
// only carry the "custom_" id prefix (D-295), so both signals count.
export const isCustomPOLine = (line = {}) =>
  line.is_custom_item === true ||
  String(line.item_id || "").trim().startsWith("custom_");

// A short text that changes if the PO's lines change (amend, cancel, qty).
// The screen records it when the PO is opened; the service compares it with
// the PO it reads inside the transaction — a PO edited in another tab can
// never be received with stale line positions.
export const poFingerprint = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((l) => [
      String(l?.item_id || "").trim(),
      Number(l?.ordered_qty || 0),
      l?._cancelled ? 1 : 0,
    ].join("|"))
    .join(";");

/**
 * Finds the Item Master record a PO line refers to (CORE_RULES §81).
 * 1. the line's canonical item_id (ITEM-…)
 * 2. legacy lines that stored the Firestore doc id instead
 * 3. item_code — only if exactly ONE item has it
 * 4. item_name — only if exactly ONE item has it
 * A line that carries a canonical id ("ITEM-…" / "custom_…") which is NOT
 * found is never guessed by code or name — that guess is exactly how stock
 * went to the wrong copper pipe (D-309). Returns null → the GRN is blocked.
 * P4-1: the rule lives in itemMatch.js (matchItem) — an item_id held by 2+
 * items picks the single active one, otherwise null (never the first in the list).
 */
export const findMasterMatch = (masterList = [], line = {}) => matchItem(masterList, line).item;

// Totals the service and the screen both need — always from the saved lines.
export const summarizeGRNItems = (items = []) => {
  let grandTotal = 0;
  let overQty = 0;
  let cancelledQty = 0;
  let receivedLines = 0;
  for (const it of items) {
    if (!it || it._cancelled) continue;
    grandTotal   += Number(it.received_qty || 0) * Number(it.rate || 0);
    overQty      += Number(it.over_qty || 0);
    cancelledQty += Number(it.cancelled_qty || 0);
    if (Number(it.received_qty || 0) > 0) receivedLines += 1;
  }
  return {
    grandTotal:   roundMoney(grandTotal),
    overQty,
    cancelledQty,
    receivedLines,
    hasOver:      overQty > 0,
  };
};

/**
 * Builds everything one store GRN will save, from:
 *   poItems    — the PO's items[] exactly as stored (incl. _cancelled lines)
 *   uiLines    — the screen's lines: PO lines carry _poIndex (their index in
 *                poItems) and _rowKey; extra items carry _extra + _rowKey
 *   rates      — { [_rowKey]: rate typed on the screen }
 *   masterList — fresh Item Master list
 *   mode       — "full" | "short_close"
 *   reason     — short close reason (text for the ledger remarks)
 *
 * Returns { errors, updatedItems, stockLines, totals }.
 * errors = { unmatched: [names], missingRate: [names], shortInFull: bool,
 *            nothingReceived: bool, badQty: ["name: reason"] } — the screen
 *            must stop if any is set. badQty (D-291 P3d-4): a counted unit
 *            (Nos, Set ...) received with decimals (units.js).
 */
export const buildStoreGRN = ({
  poItems = [],
  uiLines = [],
  rates = {},
  masterList = [],
  mode = "full",
  reason = "",
}) => {
  const isShortClose = mode === "short_close";
  const errors = { unmatched: [], missingRate: [], shortInFull: false, nothingReceived: false, badQty: [] };
  const checkQty = (label, qty, unit) => {
    const q = checkQtyForUnit(qty, unit);
    if (!q.ok) errors.badQty.push(`${label}: ${q.reason}`);
  };
  const updatedItems = [];
  const stockLines = [];
  let anyReceived = false;

  const rateOf = (rowKey) => {
    const r = Number(rates?.[rowKey]);
    return Number.isFinite(r) && r > 0 ? r : 0;
  };

  // ── PO lines ──
  poItems.forEach((pItem, idx) => {
    if (pItem?._cancelled) { updatedItems.push(pItem); return; }

    const ui         = uiLines.find((u) => !u._extra && u._poIndex === idx);
    const rowKey     = ui?._rowKey || `po-${idx}`;
    const receiveNow = Math.max(0, Number(ui?.receive_now_qty || 0));
    const ordered    = Number(pItem.ordered_qty || 0);
    const cancelled  = receiveNow < ordered ? ordered - receiveNow : 0;
    const overQty    = receiveNow > ordered ? receiveNow - ordered : 0;
    const status     = getItemGRNStatus(receiveNow, ordered);
    const rate       = rateOf(rowKey);
    const label      = pItem.item_name || pItem.item_id || "Unknown item";

    if (cancelled > 0 && !isShortClose) errors.shortInFull = true;
    if (receiveNow > 0) {
      anyReceived = true;
      if (!rate) errors.missingRate.push(label);
      checkQty(label, receiveNow, ui?.unit || pItem.unit);
    }

    // pending_qty / item_receive_status / amount: the GRN closes the PO
    // (grn_locked), so nothing stays pending — same values Direct Site
    // finalize already writes (directSitePurchaseService). Before D-292 the
    // store GRN left them "pending" / 10 / 0.
    updatedItems.push({
      ...pItem,
      received_qty:        receiveNow,
      cancelled_qty:       isShortClose ? cancelled : 0,
      over_qty:            overQty,
      pending_qty:         0,
      item_grn_status:     status,
      item_receive_status: "completed",
      rate,
      amount:              roundMoney(receiveNow * rate),
    });

    if (receiveNow <= 0 || isCustomPOLine(pItem)) return;

    const master = findMasterMatch(masterList, pItem);
    if (!master || !String(master.item_id || "").trim()) {
      errors.unmatched.push(label);
      return;
    }

    // Purchase unit → stock unit (e.g. 10 Box × 12 = 120 Nos). The rate is
    // divided by the same factor, so last_rate stays a per-stock-unit rate.
    const conv      = master.conversion;
    const converted = !!(conv && ui?.unit && ui.unit === conv.purchase_unit && Number(conv.factor) > 0);
    const factor    = converted ? Number(conv.factor) : 1;

    stockLines.push({
      item_id:        String(master.item_id).trim(),
      item_code:      master.item_code || "",
      item_name:      master.item_name || label,
      category:       master.category || "MATERIAL",
      unit:           converted ? (conv.base_unit || master.unit || "nos") : (master.unit || "nos"),
      qty:            receiveNow * factor,
      rate:           rate ? rate / factor : 0,
      reference_type: isShortClose ? "purchase_short_close_grn" : "purchase_grn",
      remarks:        isShortClose
        ? `Short Close: ${reason}`
        : (overQty > 0 ? `GRN — Over receive (+${overQty})` : "GRN Material Receive"),
    });
  });

  // ── Extra items (not in the PO) ──
  for (const ui of uiLines.filter((u) => u._extra)) {
    const receiveNow = Math.max(0, Number(ui.receive_now_qty || 0));
    if (receiveNow <= 0) continue;
    anyReceived = true;

    const rate  = rateOf(ui._rowKey);
    const label = ui.item_name || ui.item_id || "Extra item";
    if (!rate) errors.missingRate.push(label);
    checkQty(label, receiveNow, ui.unit);

    updatedItems.push({
      item_id:         ui.item_id   || "",
      item_code:       ui.item_code || "",
      item_name:       ui.item_name || "",
      category:        ui.category  || "",
      ordered_qty:     0,
      received_qty:    receiveNow,
      cancelled_qty:   0,
      over_qty:        receiveNow,
      pending_qty:     0,
      item_grn_status: "over",
      item_receive_status: "completed",
      unit:            ui.unit || "nos",
      _extra:          true,
      rate,
      amount:          roundMoney(receiveNow * rate),
    });

    // An extra item always comes from the Item Master search, so it must be
    // found by its item_id (or legacy doc id) — never by code or name.
    const id = String(ui.item_id || "").trim();
    const master = id
      ? masterList.find((m) => String(m.item_id || "").trim() === id) ||
        masterList.find((m) => String(m.id || "").trim() === id)
      : null;
    if (!master || !String(master.item_id || "").trim()) {
      errors.unmatched.push(label);
      continue;
    }

    stockLines.push({
      item_id:        String(master.item_id).trim(),
      item_code:      master.item_code || "",
      item_name:      master.item_name || label,
      category:       master.category || "MATERIAL",
      unit:           master.unit || "nos",
      qty:            receiveNow,
      rate,
      reference_type: isShortClose ? "purchase_short_close_extra_grn" : "purchase_extra_grn",
      remarks:        isShortClose
        ? `Short Close — Extra item: ${reason}`
        : "Extra item — not in original PO",
    });
  }

  // A Short Close may legitimately receive nothing (vendor never delivered —
  // close the PO). A normal GRN must receive something.
  if (!anyReceived && !isShortClose) errors.nothingReceived = true;

  return {
    errors,
    updatedItems,
    stockLines,
    totals: summarizeGRNItems(updatedItems),
  };
};

export const hasGRNErrors = (errors = {}) =>
  (errors.unmatched || []).length > 0 ||
  (errors.missingRate || []).length > 0 ||
  !!errors.shortInFull ||
  !!errors.nothingReceived ||
  (errors.badQty || []).length > 0;